import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import TooltipContent from './TooltipContent.js';
import Constants from '../../flux/Constants.js';
import { isTouchDevice } from '../../utils/CommonUtils.js';

const Tooltip = ({ targetRef, ...tooltipProps }) => {
	const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
	const timeoutRef = useRef(null);

	useEffect(() => {
		const element = targetRef?.current;
		if (isTouchDevice() || !element) {
			return;
		}

		const setHoverState = (state) => {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout(() => {
				setShouldShowTooltip(state);
			}, 100);
		};

		const handleMouseEnter = () => setHoverState(true);
		const handleMouseLeave = () => setHoverState(false);

		element.addEventListener('mouseenter', handleMouseEnter);
		element.addEventListener('mouseleave', handleMouseLeave);
		element.addEventListener('focusin', handleMouseEnter);
		element.addEventListener('focusout', handleMouseLeave);

		return () => {
			clearTimeout(timeoutRef.current);
			element.removeEventListener('mouseenter', handleMouseEnter);
			element.removeEventListener('mouseleave', handleMouseLeave);
			element.removeEventListener('focusin', handleMouseEnter);
			element.removeEventListener('focusout', handleMouseLeave);
		};
	}, [targetRef]);

	if (isTouchDevice() || !targetRef?.current) {
		return null;
	}

	return ReactDOM.createPortal(
		<TooltipContent
			shouldShowTooltip={shouldShowTooltip}
			setShouldShowTooltip={setShouldShowTooltip}
			targetRef={targetRef.current}
			{...tooltipProps}
		/>,
		document.body,
	);
};

Tooltip.propTypes = {
	targetRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
	content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
	id: PropTypes.string,
	className: PropTypes.string,
	defaultIsVisible: PropTypes.bool,
	hideAfter: PropTypes.number,
	position: PropTypes.oneOf(Object.values(Constants.Tooltip.place)),
};

export default Tooltip;
